import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CloudLoader from '../CloudLoader';
import axios from 'axios';
import LogoutModal from '../LogoutModal';

import config from '../config';
const API = config.API_BASE;

function EmployeeDashboard() {
  const [profile, setProfile] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(true);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const navigate = useNavigate();
  const userId = sessionStorage.getItem('userId');

  useEffect(() => {
  fetchProfile();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/employees/${userId}`);
      setProfile(res.data);
    } catch(e) { console.error(e); }
    setLoading(false);
  };

  const handleLogout = () => {
    sessionStorage.clear();
    navigate('/');
  };

  if (loading) return (
     <div className="min-h-screen bg-slate-100 flex items-center justify-center">
    <CloudLoader message="Loading your profile..." />
  </div>
  );

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Navbar */}
      <nav className="bg-slate-900 text-white px-6 py-4 flex items-center justify-between shadow-lg">
        <div className="flex items-center gap-3">
          <img src="/cloudrecords-logo.svg" alt="CloudRecords" className="w-8 h-8" />
          <div>
            <h1 className="font-bold text-lg leading-tight">CloudRecords</h1>
            <p className="text-xs text-slate-400">Employee Dashboard</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-slate-300">👤 {userId}</span>
          <button onClick={() => setShowLogoutModal(true)} className="bg-red-600 hover:bg-red-700 text-white text-sm px-4 py-2 rounded-lg transition">
  Logout
</button>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-8">

        {/* Welcome Card */}
        <div className="bg-gradient-to-r from-green-600 to-green-800 rounded-2xl p-6 mb-8 text-white shadow-lg">
          <p className="text-green-200 text-sm mb-1">Welcome back,</p>
          <h2 className="text-2xl font-bold">{profile?.name || userId}</h2>
          <div className="flex gap-6 mt-3">
            <span className="text-green-200 text-sm">🏢 {profile?.department || '-'}</span>
            <span className="text-green-200 text-sm">💼 {profile?.designation || '-'}</span>
            <span className="text-green-200 text-sm">🆔 {userId}</span>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
            <p className="text-slate-500 text-sm">Department</p>
            <p className="text-2xl font-bold text-green-600 mt-1">{profile?.department || '-'}</p>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
            <p className="text-slate-500 text-sm">Designation</p>
            <p className="text-2xl font-bold text-blue-600 mt-1">{profile?.designation || '-'}</p>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
            <p className="text-slate-500 text-sm">Monthly Salary</p>
            <p className="text-2xl font-bold text-purple-600 mt-1">₹{profile?.salary?.toLocaleString() || '-'}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {['profile', 'payroll'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-5 py-2.5 rounded-xl text-sm font-medium capitalize transition ${
                activeTab === tab
                  ? 'bg-green-600 text-white shadow'
                  : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
              }`}
            >
              {tab === 'profile' ? '👤 My Profile' : '💰 Payroll Info'}
            </button>
          ))}
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <h2 className="font-semibold text-slate-800 mb-6">My Profile</h2>
            {profile ? (
              <div className="grid grid-cols-2 gap-6">
                {[
                  { label: 'Full Name', value: profile.name },
                  { label: 'Employee ID', value: profile.employeeId },
                  { label: 'Department', value: profile.department },
                  { label: 'Designation', value: profile.designation },
                  { label: 'Email', value: profile.email },
                  { label: 'Joined On', value: profile.createdAt ? profile.createdAt.split('T')[0] : '-' },
                ].map((item, i) => (
                  <div key={i} className="bg-slate-50 rounded-xl p-4">
                    <p className="text-xs text-slate-400 mb-1">{item.label}</p>
                    <p className="font-medium text-slate-800">{item.value || '-'}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-400">Profile not found. Please contact admin.</p>
            )}
          </div>
        )}

        {/* Payroll Tab */}
        {activeTab === 'payroll' && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <h2 className="font-semibold text-slate-800 mb-6">Payroll Information</h2>
            {profile ? (
              <div className="grid grid-cols-2 gap-6">
                {[
                  { label: 'Basic Salary', value: `₹${profile.salary?.toLocaleString()}` },
                  { label: 'HRA (20%)', value: `₹${Math.round(profile.salary * 0.20)?.toLocaleString()}` },
                  { label: 'DA (10%)', value: `₹${Math.round(profile.salary * 0.10)?.toLocaleString()}` },
                  { label: 'PF Deduction (12%)', value: `- ₹${Math.round(profile.salary * 0.12)?.toLocaleString()}` },
                  { label: 'Tax Deduction (5%)', value: `- ₹${Math.round(profile.salary * 0.05)?.toLocaleString()}` },
                  { 
                    label: 'Net Salary', 
                    value: `₹${Math.round(profile.salary + (profile.salary * 0.20) + (profile.salary * 0.10) - (profile.salary * 0.12) - (profile.salary * 0.05))?.toLocaleString()}` 
                  },
                ].map((item, i) => (
                  <div key={i} className={`rounded-xl p-4 ${i === 5 ? 'bg-green-50 border-2 border-green-200' : 'bg-slate-50'}`}>
                    <p className="text-xs text-slate-400 mb-1">{item.label}</p>
                    <p className={`font-bold text-lg ${i === 5 ? 'text-green-600' : 'text-slate-800'}`}>{item.value || '-'}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-400">Payroll data not found. Please contact admin.</p>
            )}
          </div>
        )}
      </div>

{showLogoutModal && (
  <LogoutModal
    onConfirm={() => { setShowLogoutModal(false); handleLogout(); }}
    onCancel={() => setShowLogoutModal(false)}
  />
)}

    </div>
  );
}

export default EmployeeDashboard;